// The React Query cache is persisted to IndexedDB through JSON, and
// `isPersistSafe` (queryPersist.ts) refuses any query whose data holds a
// Map / Set. So a queryFn caches PLAIN rows and a module-level `select`
// builds the Map / Set the screen reads — consumers see the same shape as
// before. These are those pure builders (one place, so each is testable).

/** [{pick_id, …}] → Map keyed by pick_id (harvest-pick-units). */
export function mapByKey<T, K extends string>(rows: readonly T[], key: (r: T) => K): Map<K, T> {
  return new Map(rows.map(r => [key(r), r]))
}

/** Rows → Set of one string column (tenant-worker-numbers). */
export function workerNumberSet(rows: readonly { worker_number: string }[]): Set<string> {
  return new Set(rows.map(w => w.worker_number))
}

/** Leave rows → worker_number → leave_type (labour-attendance-leave, day). */
export function leaveByWorker(rows: readonly { worker_number: string; leave_type: string }[]): Map<string, string> {
  return new Map(rows.map(r => [r.worker_number, r.leave_type]))
}

/** One row of the delivery_tipping_summary RPC — the CACHED shape. */
export type TippingSummaryRow = { delivery_id: string; kg_tipped: number | string; last_tipped_at: string | null }

/** RPC rows → `{ kg, latest }` Maps (deliveries-tipped-kg; Deliveries + DeliveriesReport). */
export function tippingFromRows(rows: readonly TippingSummaryRow[] | null | undefined) {
  const kg     = new Map<string, number>()
  const latest = new Map<string, string>()
  for (const r of rows ?? []) {
    kg.set(r.delivery_id, Number(r.kg_tipped) || 0)
    if (r.last_tipped_at) latest.set(r.delivery_id, r.last_tipped_at)
  }
  return { kg, latest }
}

/** run_deliveries rows (runs embed may arrive as object or array) → delivery_id → run links. */
export function runsByDeliveryFromRows<R extends { run_id: string }>(
  rows: readonly { delivery_id: string; runs: R | R[] | null }[],
): Map<string, R[]> {
  const map = new Map<string, R[]>()
  for (const r of rows) {
    const run = Array.isArray(r.runs) ? r.runs[0] : r.runs
    if (!run?.run_id) continue
    const existing = map.get(r.delivery_id) ?? []
    existing.push(run)
    map.set(r.delivery_id, existing)
  }
  return map
}

/** bin_loans rows → delivery_id → loans, via the addition → delivery map. */
export function loansByDeliveryFromRows<L extends { delivery_addition_id: string | null }>(
  rows: readonly L[],
  additionToDelivery: ReadonlyMap<string, string>,
): Map<string, L[]> {
  const map = new Map<string, L[]>()
  for (const r of rows) {
    const did = r.delivery_addition_id ? additionToDelivery.get(r.delivery_addition_id) : undefined
    if (!did) continue
    const existing = map.get(did) ?? []
    existing.push(r)
    map.set(did, existing)
  }
  return map
}
